/**
 * Dashboard (spec §8.3): KPI row, two charts, two quick lists, all filterable.
 *
 * Chart choices worth stating, since they differ from the spec's sketch:
 *
 * • Premium vs commission is a **stacked** column, not two side-by-side series.
 *   Commission is a slice *of* the premium (~15–18%), so plotting them as peers would
 *   need two y-scales — the one chart mistake that reliably misleads. Stacked, the
 *   column height is the premium, the coloured segment is the agency's share, and both
 *   read off a single axis.
 *
 * • Distribution by product is a ranked **horizontal bar**, not the spec's pie. There
 *   are ten product types with long Greek names; a ten-slice pie is unreadable and
 *   cannot be labelled. Ranked bars sort by the thing the reader cares about and give
 *   the names room.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  BellRing,
  CalendarClock,
  CircleDollarSign,
  FileText,
  FileWarning,
  ShieldAlert,
  Wallet,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { AXIS_STYLE, ChartCard, ChartLegend, ChartTable, ChartTooltip, useChartColors } from '@/components/charts/primitives';
import { EmptyState, PageHeader } from '@/components/common/page-header';
import { StatTile, StatTileRow } from '@/components/common/stat-tile';
import { FilterBar } from '@/components/filters/filter-bar';
import { EnumBadge } from '@/components/common/enum-badge';
import { PolicyRef } from '@/components/common/policy-ref';
import {
  useBreakdown,
  useDashboardOverdueReminders,
  useDashboardRenewalsList,
  useDashboardSummary,
  useInsuranceCompanies,
  useMonthlySeries,
  useToday,
} from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';
import { entityRoute } from '@/lib/registry/navigation';
import { useListState } from '@/hooks/use-list-state';
import type { DashboardQuery } from '@ias/shared';
import { cn } from '@/lib/utils';

/**
 * The period presets.
 *
 * `all` is a value rather than the absence of one, because the absence is what the 30-day
 * default fills in: without it, "clear the dates" and "I have not chosen yet" are the same
 * URL, and clearing would silently snap back to 30 days.
 */
const PERIODS = ['30d', '90d', '365d', 'all', 'custom'] as const;
type Period = (typeof PERIODS)[number];

const DEFAULT_PERIOD: Period = '30d';

/** Local calendar date, not `toISOString()` — that renders the UTC day, which is yesterday
 *  in Athens for anything after 02:00 in summer, so «σήμερα» would be off by one. */
const isoDay = (value: Date) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;

function periodRange(
  period: Period,
  custom: { dateFrom?: string; dateTo?: string },
): { dateFrom?: string; dateTo?: string } {
  if (period === 'all') return {};
  if (period === 'custom') return { dateFrom: custom.dateFrom, dateTo: custom.dateTo };

  const days = period === '30d' ? 30 : period === '90d' ? 90 : 365;
  const to = new Date();
  const from = new Date(to);
  // Inclusive of today, so «30 ημέρες» spans thirty calendar days rather than thirty-one.
  from.setDate(from.getDate() - (days - 1));
  return { dateFrom: isoDay(from), dateTo: isoDay(to) };
}

export function DashboardPage() {
  const { t, money, moneyCompact, number, date, percent, relativeDays, locale } = useI18n();
  const navigate = useNavigate();
  const colors = useChartColors();

  const list = useListState();
  // Filters come out of the URL as plain strings; the server re-validates them against
  // the same enum, so narrowing here is a type assertion rather than a second check.
  const { period: rawPeriod, dateFrom, dateTo, ...dimensions } = list.filters;
  const period: Period = PERIODS.includes(rawPeriod as Period) ? (rawPeriod as Period) : DEFAULT_PERIOD;
  const range = React.useMemo(
    () => periodRange(period, { dateFrom, dateTo }),
    [period, dateFrom, dateTo],
  );

  /*
   * Write the default into the URL rather than leaving it implicit. The select can only show
   * what the URL holds, so an unwritten default would put «Περίοδος» above figures that are
   * in fact scoped to thirty days — the same silence this whole change is undoing. Clearing
   * the filters lands back here, which is the right destination for a default.
   */
  const { setFilter } = list;
  React.useEffect(() => {
    if (!rawPeriod) setFilter('period', DEFAULT_PERIOD);
  }, [rawPeriod, setFilter]);

  /**
   * What the two money tiles say they cover. «Προσαρμοσμένο» resolves to the dates chosen —
   * and, until one is, to «όλο το διάστημα», because that is what an empty custom range
   * actually computes. Naming the control instead of the result is how the blank date boxes
   * misled in the first place.
   */
  const periodLabel = (() => {
    const named = (value: Period) =>
      t(`dashboard.period.${value}`).toLocaleLowerCase(locale === 'el' ? 'el-GR' : 'en-GB');
    if (period !== 'custom') return named(period);
    const chosen = [range.dateFrom, range.dateTo].filter(Boolean) as string[];
    return chosen.length ? chosen.map(date).join(' – ') : named('all');
  })();

  /*
   * Only the summary takes the period, and only its two money figures use it — see
   * DashboardService.SummaryAsync. The charts take the dimension filters alone: the monthly
   * series is a twelve-month view by definition and a 30-day range would crush it to one
   * column, and the product breakdown describes the live book, which is the same thing
   * «Ενεργά συμβόλαια» above it counts. So the rule the page follows, and states in its
   * subtitle, is: the period moves the two amounts; everything else is the position now.
   */
  const summary = useDashboardSummary({ ...dimensions, ...range } as DashboardQuery);
  const series = useMonthlySeries({ ...dimensions, months: 12 } as DashboardQuery & { months: number });
  const byType = useBreakdown('insurance-type', dimensions as DashboardQuery);
  const renewals = useDashboardRenewalsList();
  const overdue = useDashboardOverdueReminders();
  // D11 review outcome: «Η μέρα μου» retired as a page; the parts of it that are general
  // information about the whole system — lost business, the collections position, expiring
  // identity documents — merged here instead.
  const today = useToday(false);
  const { data: insurers } = useInsuranceCompanies();

  const data = summary.data;

  /* ── premium / commission by month ─────────────────────────────────────── */

  /**
   * `en-GB` renders September as "Sept" — four letters where every other month gets
   * three, so one bar's label sticks out and the axis stops looking like a grid. ICU is
   * right (that is the British abbreviation), but a chart axis wants uniform ticks, so
   * Latin labels are clipped to three. Greek is left alone: its abbreviations are
   * already even and clipping accented Greek mid-word would be worse than the problem.
   */
  const monthLabel = (period: string) => {
    const formatted = new Intl.DateTimeFormat(locale === 'el' ? 'el-GR' : 'en-GB', {
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    }).format(new Date(`${period}-01T00:00:00Z`));

    return locale === 'el' ? formatted : formatted.replace(/^([A-Za-z]{3})[A-Za-z]*/, '$1');
  };

  const seriesData = (series.data ?? []).map((point) => ({
    period: point.period,
    label: monthLabel(point.period),
    commission: Number(point.commission),
    // The stack sums to the premium, so the remainder is what goes to the insurer.
    net: Math.max(0, Number(point.premium) - Number(point.commission)),
    premium: Number(point.premium),
  }));

  const legendItems = [
    { label: t('dashboard.commission'), color: colors.series1 ?? '#2a78d6' },
    { label: t('dashboard.netPremium'), color: colors.muted ?? '#ddd' },
  ];

  /* ── distribution by product ───────────────────────────────────────────── */

  const typeData = (byType.data ?? [])
    .map((row) => ({
      key: row.key,
      label: t(`enum.insuranceType.${row.key}`),
      premium: Number(row.premium),
      count: row.count,
    }))
    .sort((a, b) => b.premium - a.premium);

  const maxPremium = Math.max(1, ...typeData.map((row) => row.premium));

  return (
    <>
      <PageHeader title={t('dashboard.title')} description={t('dashboard.subtitle')} />

      <FilterBar
        className="mb-5"
        listState={list}
        searchPlaceholder={t('action.search')}
        filters={[
          /*
           * A named period rather than two bare date boxes.
           *
           * Empty date inputs render as the browser's own placeholder — in this locale, a
           * greyed «21/09/2026» — which reads as a range of one day that is being applied.
           * It was not: nothing was filtered, and the premium tile was quietly showing the
           * whole book. A preset says what it is in words, and «Προσαρμοσμένο» is the only
           * state where the two dates appear at all.
           */
          {
            key: 'period',
            type: 'options',
            label: t('dashboard.period'),
            clearable: false,
            options: PERIODS.map((value) => ({ value, label: t(`dashboard.period.${value}`) })),
          },
          ...(period === 'custom'
            ? ([
                { key: 'dateFrom', type: 'date', label: t('dashboard.writtenFrom') },
                { key: 'dateTo', type: 'date', label: t('dashboard.writtenTo') },
              ] as const)
            : []),
          {
            key: 'insuranceCompanyId',
            type: 'options',
            label: t('policy.company'),
            options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
          },
          { key: 'insuranceType', type: 'enum', enum: 'insuranceType', label: t('policy.insuranceType') },
        ]}
      />

      {/* Win-back strip, inherited from «Η μέρα μου»: lost business is the cheapest business
          to regain, and it belongs in front of whoever opens the system. */}
      {today.data && today.data.lapsed.count > 0 && (
        <Card className="border-warning/45 bg-warning/[0.07] mb-4">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <p className="text-sm">
              {t('today.lapsed', { n: today.data.lapsed.count, amount: moneyCompact(today.data.lapsed.premium) })}
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/policies?status=expired&paymentStatus=unpaid,partially_paid')}
            >
              {t('today.lapsedAction')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* KPI row — spec §8.3 lists exactly these six. */}
      <StatTileRow className="mb-5">
        <StatTile
          label={`${t('dashboard.totalPremium')} · ${periodLabel}`}
          value={moneyCompact(data?.totalPremium)}
          delta={data?.deltas.totalPremium}
          icon={Wallet}
          hint={money(data?.totalPremium)}
          isLoading={summary.isLoading}
        />
        {/*
          The hint is the full amount when the book is complete, and the caveat when it is not.
          Commission is calculated on the ΚΑΘΑΡΑ alone, so a policy with none earns 0,00 € by
          definition — with 4.894 imported policies in that state this tile reads far below what
          the agency actually earns, and a bare euro figure would be a false claim rather than a
          partial one. Clicking through goes to the list of policies still waiting for them.
        */}
        <StatTile
          label={`${t('dashboard.totalCommission')} · ${periodLabel}`}
          value={moneyCompact(data?.totalCommission)}
          delta={data?.deltas.totalCommission}
          icon={CircleDollarSign}
          hint={
            (data?.policiesMissingNetPremium ?? 0) > 0
              ? t('commission.missingNet', { count: number(data!.policiesMissingNetPremium) })
              : money(data?.totalCommission)
          }
          tone={(data?.policiesMissingNetPremium ?? 0) > 0 ? 'warning' : 'default'}
          onClick={
            (data?.policiesMissingNetPremium ?? 0) > 0
              ? () => navigate('/policies?missingNetPremium=true')
              : undefined
          }
          isLoading={summary.isLoading}
        />
        {/* No delta here. `deltas.activePolicies` counts policies *written* in the window
            against the one before it — a period figure, which cannot describe a tile that
            now deliberately reports the whole live book. */}
        <StatTile
          label={t('dashboard.activePolicies')}
          value={number(data?.activePolicies)}
          icon={FileText}
          isLoading={summary.isLoading}
          onClick={() => navigate('/policies?status=active')}
        />
        <StatTile
          label={t('dashboard.upcomingRenewals')}
          value={number(data?.upcomingRenewals)}
          icon={CalendarClock}
          // Spec §8.3: highlighted once the queue passes ten.
          tone={(data?.upcomingRenewals ?? 0) > 10 ? 'warning' : 'default'}
          isLoading={summary.isLoading}
          onClick={() => navigate('/renewals')}
        />
        {/*
          The count and the amount beneath it are measured differently on purpose: the
          count is *policies* still owing (excluding cancelled and expired ones, which
          nobody is going to collect), while the amount is the sum of unpaid *instalments*.
          Both are the right basis for their own question; unlabelled and stacked, they
          looked like two versions of one number that failed to agree.
        */}
        <StatTile
          label={t('dashboard.unpaidPolicies')}
          value={number(data?.unpaidPolicies)}
          hint={t('dashboard.unpaidPoliciesHint')}
          icon={AlertTriangle}
          tone={(data?.unpaidPolicies ?? 0) > 0 ? 'critical' : 'default'}
          isLoading={summary.isLoading}
          onClick={() => navigate('/policies?paymentStatus=unpaid,partially_paid')}
          footer={
            data ? (
              <span className="text-muted-soft">
                {moneyCompact(data.outstandingReceivables)} {t('dashboard.receivablesBasis')}
              </span>
            ) : undefined
          }
        />
        <StatTile
          label={t('dashboard.openClaims')}
          value={number(data?.openClaims)}
          icon={ShieldAlert}
          isLoading={summary.isLoading}
          onClick={() => navigate('/claims?status=open,in_progress,waiting_documents')}
          footer={
            data?.retentionRate != null ? (
              <span className="text-muted-foreground">
                {t('dashboard.retention')} {percent(data.retentionRate)}
              </span>
            ) : undefined
          }
        />
      </StatTileRow>

      {/* Charts */}
      <div className="mb-5 grid gap-4 xl:grid-cols-5">
        <ChartCard
          className="xl:col-span-3"
          title={t('dashboard.premiumByMonth')}
          description={t('dashboard.premiumByMonthHint')}
          isLoading={series.isLoading}
          isEmpty={!seriesData.length}
          action={<ChartLegend items={legendItems} className="mr-1 hidden sm:flex" />}
          table={
            <ChartTable
              columns={[t('policy.period'), t('dashboard.commission'), t('dashboard.totalPremium')]}
              rows={seriesData.map((row) => [row.label, money(row.commission), money(row.premium)])}
            />
          }
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={seriesData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={colors.grid} strokeWidth={1} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ ...AXIS_STYLE, fill: colors.axis }}
                dy={4}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                width={52}
                tick={{ ...AXIS_STYLE, fill: colors.axis }}
                tickFormatter={(value: number) => moneyCompact(value)}
              />
              <RechartsTooltip
                cursor={{ fill: 'currentColor', opacity: 0.05 }}
                content={<ChartTooltip formatter={(value) => money(value)} />}
              />
              {/* 2px surface-coloured stroke gives the surface gap that separates the
                  two segments without drawing a border around the data. */}
              <Bar
                dataKey="commission"
                name={t('dashboard.commission')}
                stackId="premium"
                fill={colors.series1}
                stroke={colors.surface}
                strokeWidth={2}
                maxBarSize={24}
              />
              <Bar
                dataKey="net"
                name={t('dashboard.netPremium')}
                stackId="premium"
                fill={colors.muted}
                stroke={colors.surface}
                strokeWidth={2}
                maxBarSize={24}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
          <ChartLegend items={legendItems} className="mt-2 justify-center sm:hidden" />
        </ChartCard>

        <ChartCard
          className="xl:col-span-2"
          title={t('dashboard.byType')}
          description={t('dashboard.byTypeHint')}
          isLoading={byType.isLoading}
          isEmpty={!typeData.length}
          table={
            <ChartTable
              columns={[t('policy.insuranceType'), t('report.policyCount'), t('dashboard.totalPremium')]}
              rows={typeData.map((row) => [row.label, row.count, money(row.premium)])}
            />
          }
        >
          {/* Single series → sequential single hue, and no legend box: the card title
              already says what is plotted. */}
          <div className="flex h-full flex-col justify-center gap-2 pr-1">
            {typeData.slice(0, 8).map((row) => (
              <button
                key={row.key}
                type="button"
                onClick={() => navigate(`/policies?insuranceType=${row.key}`)}
                className="group grid grid-cols-[7.5rem_1fr_auto] items-center gap-2 text-left"
              >
                <span className="text-muted-foreground group-hover:text-foreground truncate text-xs transition-colors">
                  {row.label}
                </span>
                <span className="bg-muted h-4 overflow-hidden rounded-sm">
                  <span
                    className="block h-full rounded-r-[4px] transition-[width]"
                    style={{
                      width: `${Math.max(2, (row.premium / maxPremium) * 100)}%`,
                      backgroundColor: colors.series1,
                    }}
                  />
                </span>
                <span className="w-16 text-right text-xs font-medium tabular">
                  {moneyCompact(row.premium)}
                </span>
              </button>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Quick lists — spec §8.3 bottom row, plus the expiring-documents list that moved
          here from «Η μέρα μου» (D11). */}
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.upcomingList')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/renewals')}>
              {t('action.view')}
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {renewals.isLoading ? (
              <ListSkeleton />
            ) : !renewals.data?.length ? (
              <EmptyState icon={CalendarClock} title={t('renewals.empty')} compact />
            ) : (
              <ul className="divide-y">
                {renewals.data.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/policies/${row.id}`)}
                      className="hover:bg-accent/50 -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2 text-left transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{row.clientName}</p>
                        <p className="text-muted-foreground flex min-w-0 items-baseline gap-1.5 text-xs">
                          <PolicyRef policyNumber={row.policyNumber} plates={row.plates} muted />
                          <span className="truncate">· {row.insuranceCompanyName}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium tabular">{money(row.premiumAmount)}</p>
                        <p className="text-muted-foreground text-xs tabular">{date(row.endDate)}</p>
                      </div>
                      <DaysLeftBadge days={row.daysLeft} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.overdueList')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/reminders?status=overdue')}>
              {t('action.view')}
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {overdue.isLoading ? (
              <ListSkeleton />
            ) : !overdue.data?.length ? (
              <EmptyState icon={BellRing} title={t('notification.empty')} compact />
            ) : (
              <ul className="divide-y">
                {overdue.data.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() =>
                        row.relatedEntityType && row.relatedEntityId
                          ? navigate(entityRoute(row.relatedEntityType, row.relatedEntityId))
                          : navigate('/reminders')
                      }
                      className="hover:bg-accent/50 -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2 text-left transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{row.title}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {row.relatedEntityLabel ?? '—'}
                        </p>
                      </div>
                      <p className="text-muted-foreground text-xs tabular">{date(row.reminderDate)}</p>
                      <Badge tone="critical" size="sm">
                        +{row.daysOverdue}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('today.expiringDocs')}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {today.isLoading ? (
              <ListSkeleton />
            ) : !today.data?.expiringDocuments.length ? (
              <EmptyState icon={FileWarning} title={t('common.empty')} compact />
            ) : (
              <ul className="divide-y">
                {today.data.expiringDocuments.map((row, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${row.clientId}`)}
                      className="hover:bg-accent/50 -mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-md px-2 py-2 text-left transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{row.clientName}</p>
                        <EnumBadge enum="clientDocumentType" value={row.documentType} size="sm" variant="dot" />
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-muted-foreground text-xs tabular">{date(row.expiresAt)}</p>
                        <p
                          className={cn(
                            'text-xs',
                            row.inDays <= 15 ? 'text-critical font-medium' : 'text-muted-foreground',
                          )}
                        >
                          {relativeDays(row.inDays)}
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/** Spec §8.3: red < 7 days, orange < 15, yellow < 30. */
function DaysLeftBadge({ days }: { days: number }) {
  const { t } = useI18n();
  const tone = days < 0 ? 'critical' : days < 7 ? 'critical' : days < 15 ? 'serious' : days < 30 ? 'warning' : 'neutral';

  /*
   * Zero is a word, not a count. «0 ημ.» reads as "no time left" — which is how an expiry
   * already past reads — when what it means is that the policy runs out today, the one day
   * in the list that needs picking up the phone.
   */
  return (
    <Badge tone={tone} size="sm" className={cn('w-16 justify-center tabular')}>
      {days < 0 ? t('common.expired') : days === 0 ? t('common.today') : `${days} ${t('common.daysShort')}`}
    </Badge>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3 py-1">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
    </div>
  );
}
